import { arrowUndoOutline, createOutline } from 'ionicons/icons';
import { cloneDeep, isNil } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useWithConfirmation } from '../../../../../../../components/button/with-confirmation';
import { useScratchpadServiceContext } from '../../../../../../../components/machine/scratchpad/scratchpad-service-context-provider';
import { getLocalizedText } from '../../../../../../../i18n/i18n.utils';
import { logger } from '../../../../../../../util/logger';
import {
  FlowUpdateMethod,
  useFlowRefContext,
} from '../../../../../shared/component/flow/flow-ref';
import { FlowItemBaseAction } from '../../../../../shared/component/flow-item/action/flow-item-base-action';
import { FlowItemCopyAction } from '../../../../../shared/component/flow-item/action/flow-item-copy-action';
import { FlowItemDeleteAction } from '../../../../../shared/component/flow-item/action/flow-item-delete-action';
import { FlowSectionEditActionBarProps } from '../../../../../shared/component/flow-item/edit/flow-section-edit-action-bar';
import { useFlowServices } from '../../../../../shared/hook/flow-service.hooks';
import { FlowItem } from '../../../../../shared/model/flow.types';
import {
  createSectionsFromFlowItems,
  instanceOfFlowItem,
} from '../../../../../shared/util/flow-item.util';
import { BeaconWeldingData } from '../../../model/beacon-welding-data';

const WeldingFlowSectionMultipassActionBar = (
  props: Omit<FlowSectionEditActionBarProps, 'sectionItem'> & {
    sectionItem: BeaconWeldingData.FlowItemMultipassSectionParent;
  }
) => {
  const { sectionItem, readOnly, onDelete, flowItems, onClosed } = props;

  const { flowAnalyticService } = useFlowServices();
  const { saveFlow, componentRegistry } = useFlowRefContext();
  const { quickActions } = componentRegistry;
  const { scratchpad } = useScratchpadServiceContext();

  const sections = createSectionsFromFlowItems(flowItems);
  const childSections = useMemo(
    () => sections.filter((s) => s?.section?.parentItem?.id === sectionItem.id),
    [sectionItem.id, sections]
  );

  const allMultipassSegments = useMemo(() => {
    const items = [
      ...(childSections.map((s) => s.section?.item) ?? []),
    ] as FlowItem[];

    return cloneDeep(items);
  }, [childSections]);

  const allMultipassItems = useMemo(() => {
    const childItems = childSections.flatMap((s) => {
      const items = s.items.map((i) => i.item);
      const sectionItem = s?.section?.item;

      return sectionItem ? [sectionItem, ...items] : items;
    });

    return [sectionItem, ...childItems] as FlowItem[];
  }, [childSections, sectionItem]);

  const originItemId = useMemo(
    () =>
      sectionItem.attributes.originalItems.find(instanceOfFlowItem)?.id ??
      'unknown',
    [sectionItem.attributes.originalItems]
  );

  const withConfirmation = useWithConfirmation({
    header: getLocalizedText('welding:multipass_undo_confirmation'),
  });

  const undoMultipass = useCallback(async () => {
    const clonedItems = cloneDeep(flowItems);

    const startIndex = clonedItems.findIndex((i) => i.id === sectionItem.id);
    const endIndex = clonedItems.findIndex(
      (i) => i.id === allMultipassItems[allMultipassItems.length - 1].id
    );

    clonedItems.splice(
      startIndex,
      endIndex - startIndex + 1,
      ...((sectionItem.attributes.originalItems as FlowItem[]) ?? [])
    );

    flowAnalyticService.trackMultipassUndo({
      scratchpadId: scratchpad.id,
      sectionItemId: sectionItem.id,
      originItemId:
        sectionItem.attributes.originalItems.find(instanceOfFlowItem)?.id ??
        'unknown',
    });

    await saveFlow(clonedItems, {
      updateMethod: FlowUpdateMethod.SERVER,
      saveAsDelta: true,
    });
  }, [
    allMultipassItems,
    flowAnalyticService,
    flowItems,
    saveFlow,
    scratchpad.id,
    sectionItem.attributes.originalItems,
    sectionItem.id,
  ]);

  const handleUndo = () => {
    withConfirmation({
      onConfirmation: async () => {
        await undoMultipass();
        if (onClosed) {
          onClosed();
        }
      },
    });
  };

  return (
    <div className="flow-section-edit-action-bar">
      <FlowItemBaseAction
        label={getLocalizedText('welding:multipass_undo')}
        icon={arrowUndoOutline}
        disabled={readOnly}
        onClick={handleUndo}
        color="var(--ion-text-color)"
      />
      <FlowItemBaseAction
        icon={createOutline}
        label={getLocalizedText('welding:multipass_edit')}
        onClick={() => {
          const hasConvertToMultipassHandler =
            quickActions &&
            !isNil(
              quickActions[
                BeaconWeldingData.FlowItemQuickAction.CONVERT_TO_MULTIPASS
              ]
            );

          if (hasConvertToMultipassHandler) {
            const { handler } =
              quickActions[
                BeaconWeldingData.FlowItemQuickAction.CONVERT_TO_MULTIPASS
              ];
            handler(sectionItem, onClosed);
          } else {
            logger.error('No handler for multipass conversion', {
              quickActions: Object.keys(quickActions ?? {}),
            });
          }
        }}
      />
      <FlowItemCopyAction
        label={`${getLocalizedText('common:copy_all')} (${
          allMultipassSegments.length
        })`}
        items={allMultipassItems}
        onCopy={() => {
          flowAnalyticService.trackMultipassCopy();
        }}
      />
      <FlowItemDeleteAction
        label={`${getLocalizedText('common:delete_all')} (${
          allMultipassSegments.length
        })`}
        disabled={readOnly}
        onClick={() => {
          if (onDelete) {
            flowAnalyticService.trackStitchDelete({
              scratchpadId: scratchpad.id,
              sectionItemId: sectionItem.id,
              originItemId,
            });
            onDelete(allMultipassItems);
          }
        }}
      />
    </div>
  );
};

export { WeldingFlowSectionMultipassActionBar };
